import * as React from "react";
import * as ReactDOM from "react-dom/client";
import * as S from "@styled";
import * as _ from "lodash";
import { Utilities } from "../shared/utilities";
import styled, { ThemeProvider } from "styled-components";
import { GlobalStyle, NavBarHeight, ThemeType } from "shared/styles/theme";
import { device } from "shared/styles/helpers";
import { getFrendsLogoSvg } from "shared/icons/svgHelpers";
import LinkWithMessageBubble from "shared/components/LinkWithMessageBubble";

// SignIn Nav definition starts
// import common stuff here for icons 
import "../shared/vendorCss"
import useUserTheme from "shared/styles/hooks/useUserTheme";
import { NavLink } from "../navigation/styled";
import StylesheetProvider from "shared/providers/StylesheetProvider";

const StyledSignInNavbar = styled.nav`
    width: 100%;
    height: ${NavBarHeight};
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    margin: 0;
    padding: 0;
    padding: 8px 10px 8px 8px;

    @media ${device.laptopL} {
        padding: 8px 10px 8px 24px;
    }

    font-size: 1rem;

    > * {
        color: black;
        padding: 14px;
    }
`

const renderSignInNav = () => {
    const rootContainer = document.getElementById("navigationbar");
    if (rootContainer) {
        const hasUser = rootContainer?.getAttribute('data-username');
        const root = ReactDOM.createRoot(rootContainer);
        root.render(
            <React.StrictMode>
                <StylesheetProvider>
                    <ThemeProvider theme={{ mode: ThemeType.light }}>
                        <GlobalStyle />
                        <StyledSignInNavbar>
                            <div dangerouslySetInnerHTML={{ __html: getFrendsLogoSvg() }}></div>
                            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                                <LinkWithMessageBubble href="https://docs.frends.com/" />
                                {hasUser && <NavLink href={"Account/SignOut"}>{FRENDS.Localizations.SignOut}</NavLink>}
                            </div>
                        </StyledSignInNavbar>
                    </ThemeProvider>
                </StylesheetProvider>
            </React.StrictMode>
        );
    }
}

renderSignInNav();
// SignIn Nav definition ends

const LoginContainer = styled(S.IndentedContainer)`
    position: absolute;
    top: 40%;
    -ms-transform: translateY(-40%);
    transform: translateY(-40%);
`

const App: React.FC<React.PropsWithChildren<unknown>> = () => {
    const [theme] = useUserTheme();
    const [signInProviders, setSignInProviders] = React.useState<ISigninProvider[]>([]);

    const [modelState, setModelState] = React.useState<ISignInContainerModelState>();

    React.useEffect(() => {
        const fetchSignInProviders = async () => {
            try {
                const fetchedSignInProviders = await Utilities.getFetch<ISigninProvider[]>("api/account/providers");
                setSignInProviders(fetchedSignInProviders);
            } catch (e) {
                console.error(e);
                Utilities.toastError(e);
            }
        }
        fetchSignInProviders();
    }, []);

    React.useEffect(() => {
        const navigationBarDataset = document.getElementById("navigationbar")?.dataset;
        const signInDataset = document.getElementById("signInContainer")?.dataset;
        if (navigationBarDataset && signInDataset) {
            const combinedModelState: ISignInContainerModelState = {
                ...signInDataset,
                ...navigationBarDataset
            }
            setModelState(combinedModelState);
        }
    }, []);

    return (
        <ThemeProvider theme={{ mode: theme }}>
            <GlobalStyle />
            <LoginContainer>
                {modelState?.error && (
                    <S.ErrorContainer>
                        <div className="alert-warning">
                            <div className="alert-label">{modelState?.error}</div>
                            {modelState.errorDescription && (
                                <div className="alert-content">Error: {modelState?.errorDescription}</div>
                            )}
                            {modelState?.isAuthenticated && (
                                <div className="alert-content">{`${FRENDS.Localizations.SignIn_ContactAdmin} You may also try to sign out and use another account.`}</div>
                            )}
                        </div>
                    </S.ErrorContainer>
                )}
                <S.PageHeaderContainer>
                    <h1>Select authentication<br /> provider to sign in</h1>
                </S.PageHeaderContainer>
                <S.ButtonGroup gap={12} asColumn={true}>
                    {signInProviders.map((provider) => (
                        <S.Button
                            as="a"
                            id={`login-provider-${provider.clientId}`}
                            style={{ width: "fit-content" }}
                            key={provider.clientId}
                            href={`api/account/signin/${provider.clientId}${window.location.search}`}
                        >
                            {provider.name} {provider.isAuthenticatedTo && `- ${FRENDS.Localizations.SignIn_SignedInAs} ${modelState?.username ?? ""}`}
                        </S.Button>
                    ))}
                </S.ButtonGroup>
            </LoginContainer>
        </ThemeProvider >
    )
}

export interface ISigninProvider {
    name: string;
    clientId: string;
    isAuthenticatedTo: boolean;
    hiddenFromLogin: boolean;
}

interface ISignInContainerModelState {
    error?: string;
    errorDescription?: string;
    isAuthenticated?: "True" | "False";
    username?: string;
}

export default App;

const signInContainer = document.getElementById("signInContainer");
if (signInContainer) {
    const root = ReactDOM.createRoot(signInContainer);
    root.render(
        <React.StrictMode>
            <StylesheetProvider>
                <App />
            </StylesheetProvider>
        </React.StrictMode>
    );
}
